import { Image } from 'expo-image';
import { ImageBackground, Platform, View } from 'react-native';
import Modal from 'react-native-modal';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import useSettingsStore from '@/stores/use-settings-store';
import { Button, Card, Text } from '@/ui';

import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

const downloadAppDark = require('../../../public/images/partners/download-app-dark.png');
const downloadAppLight = require('../../../public/images/partners/download-app-light.png');

const steps = [
  'Go to Account in the app',
  'Scroll down to find partner options',
  'Tap the “Switch to Partners” button to switch views',
];

interface DownloadAppModalProps {
  style?: any;
}

export function DownloadAppModal({ style }: DownloadAppModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const downloadAppModalSeen = useSettingsStore(
    (state) => state.downloadAppModalSeen
  );
  const setDownloadAppModalSeen = useSettingsStore(
    (state) => state.setDownloadAppModalSeen
  );

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={Platform.OS === 'web' && !downloadAppModalSeen}
      hasBackdrop
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={() => {
        setDownloadAppModalSeen(true);
      }}
    >
      <View
        style={{
          width: '100%',
          backgroundColor: theme.colors.background_2,
          gap: 24,
          borderRadius: 16,
          borderWidth: 1,
          borderColor: theme.colors.card.default.borderColor,
        }}
      >
        <ImageBackground
          source={
            UnistylesRuntime.themeName === 'dark'
              ? downloadAppDark
              : downloadAppLight
          }
          resizeMode="cover"
          style={{
            width: '100%',
            height: 200,
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}
          imageStyle={{
            width: '100%',
            height: '100%',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }}
        >
          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setDownloadAppModalSeen(true);
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </ImageBackground>

        <View
          style={{
            padding: 16,
            paddingTop: 0,
            gap: 16,
          }}
        >
          <View style={{ gap: 8 }}>
            <Text type="title2">Get The Eon App</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Scan the QR code with your phone to download the app.
            </Text>
          </View>

          <View style={styles.qrContainer}>
            <Card
              style={{
                backgroundColor: theme.colors.card.hover.backgroundColor,
                padding: 16,
              }}
            >
              <View style={{ alignItems: 'center', gap: 16 }}>
                <Text type="bodyRegular">Download the app</Text>

                <Image
                  style={{
                    width: 100,
                    height: 100,
                  }}
                  source={
                    UnistylesRuntime.themeName === 'dark'
                      ? require('@/../public/images/qr-onelink-dark.png')
                      : require('@/../public/images/qr-onelink-light.png')
                  }
                  contentFit="cover"
                />

                <Text type="bodyRegular">Available on iOS and Android</Text>
              </View>
            </Card>
          </View>

          <View>
            <Text type="title2">
              How to Access Partner Portal in the Eon App
            </Text>
          </View>

          <View style={{ gap: 16 }}>
            {steps.map((step, index) => (
              <View
                key={index}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <View
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    backgroundColor:
                      theme.colors.progressBar.active.backgroundColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.text.inverse }}
                  >
                    {index + 1}
                  </Text>
                </View>

                <Text type="bodyRegular" style={{ flex: 1 }}>
                  {step}
                </Text>
              </View>
            ))}
          </View>

          <View style={styles.buttonsMobileContainer}>
            <Button
              text="Get The App"
              type="primary"
              onPress={() => {
                window.open(
                  'https://appeon.onelink.me/nQbG/2an6kaz8',
                  '_blank'
                );
              }}
            />

            <Button
              text="Cancel"
              type="ghost"
              onPress={() => {
                setDownloadAppModalSeen(true);
              }}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    alignItems: 'center',
    justifyContent: {
      xs: 'center',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '95%',
      md: 700,
    },
  },
  qrContainer: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
  buttonsMobileContainer: {
    display: {
      xs: 'flex',
      md: 'none',
    },
  },
}));
